import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@localagent/ui/components/dialog';
import { Button } from '@localagent/ui/components/button';
import { Input } from '@localagent/ui/components/input';
import { Label } from '@localagent/ui/components/label';
import { toast } from '@localagent/ui/components/sonner';
import { useAuthenticateClientCredentials } from '../../hooks/useIntegrations';

interface ClientCredentialsAuthDialogProps {
  connectionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function ClientCredentialsAuthDialog({ connectionId, open, onOpenChange, onSuccess }: ClientCredentialsAuthDialogProps) {
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const authCredentials = useAuthenticateClientCredentials();

  const handleSubmit = () => {
    if (!clientId.trim() || !clientSecret.trim()) return;
    authCredentials.mutate(
      { connectionId, clientId, clientSecret },
      {
        onSuccess: () => {
          toast.success('Client credentials authenticated successfully');
          setClientId('');
          setClientSecret('');
          onOpenChange(false);
          onSuccess?.();
        },
        onError: (err) => {
          toast.error(`Authentication failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Client Credentials Authentication</DialogTitle>
          <DialogDescription>Enter your client credentials to authenticate this connection.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="client-id">Client ID</Label>
            <Input
              id="client-id"
              placeholder="Enter client ID"
              value={clientId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setClientId(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="client-secret">Client Secret</Label>
            <Input
              id="client-secret"
              type="password"
              placeholder="Enter client secret"
              value={clientSecret}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setClientSecret(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!clientId.trim() || !clientSecret.trim() || authCredentials.isPending}>
            {authCredentials.isPending ? 'Authenticating...' : 'Authenticate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
